import dynamic from 'next/dynamic';
import { useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { MdOutlineCalendarMonth } from 'react-icons/md';
import Tag from '../../atoms/Tag';
import ShareButton from '../../atoms/ShareButton';
import { dateFormatter, triggerNativeMessage } from 'ui/helpers/utils';
import useIsNativeView from 'ui/hooks/useIsNativeView';
import { IRootState } from '../../../store';
import { Article } from '../../../interface';
import { usePathname } from 'next/navigation';
import useImpressionPromo from '../../../hooks/emarsys/promo/useImpressionPromo';
import { IBreadcrumbs } from '../../molecules/Breadcrumbs';

const ModalShare = dynamic(() => import('ui/components/atoms/ModalShare'), {
  ssr: false,
});

interface IDetailPromoBodySectionProps {
  promo:
    | {
        tag?: {
          tagName?: string;
        };
        startDate?: string;
        endDate?: string;
        titlePage?: string;
        detail?: {
          detailContent?: string;
        };
        detailContent?: string;
      }
    | Article;
  enableSharing?: boolean;
  screenSizeData?: {
    height: number;
    mobile: boolean;
    width: number;
    isMobileScreen?: boolean;
  };
  actionClick?: (buttonName: string) => void;
  impressionData?: {
    pageTitle: string;
    breadCrumb: IBreadcrumbs[];
  };
}

const DetailPromoBodySection = ({
  promo,
  enableSharing = false,
  screenSizeData = { height: 673, mobile: false, width: 1920 },
  actionClick,
  impressionData = { pageTitle: '', breadCrumb: [] },
}: IDetailPromoBodySectionProps) => {
  const impressionRef = useRef(null);
  // Add enableSharing prop
  const [modalVisible, setModalVisible] = useState(false);
  const screenSize = enableSharing
    ? screenSizeData
    : useSelector((state: IRootState) => state.page.screenSize);

  const isNativeView = useIsNativeView();
  const pathname = usePathname();

  const formattedStartDate = useMemo(() => {
    return dateFormatter(promo.startDate, enableSharing);
  }, [promo.startDate]);

  const formattedEndDate = useMemo(() => {
    return dateFormatter(promo.endDate, enableSharing);
  }, [promo.endDate]);

  // Trigger share system on mobile
  const handleShare = async () => {
    if (screenSize.mobile && navigator.share) {
      try {
        await navigator.share({
          title: promo.titlePage,
          text: 'Check out this promo!',
          url: typeof window !== 'undefined' ? window.location.href : '',
        });
      } catch (error) {
        console.error('Share error:', error);
        // open modal if failed open navigator.share() except cancelation
        if (!error.toString().includes('AbortError')) {
          setModalVisible(true);
        }
      }
    } else {
      setModalVisible(true);
    }
  };

  const handleShareButtonClicked = (message, titlePage) => {
    triggerNativeMessage(message, { titlePage });

    if (isNativeView) {
      setModalVisible(false);
    } else if (screenSize.mobile) {
      handleShare();
    } else {
      setModalVisible(true);
    }
  };

  useImpressionPromo(impressionRef, promo as Article, impressionData.breadCrumb, 'detail promosi');

  return (
    <>
      {/* Detail promo body section */}
      <section className="my-4 lg:my-6" ref={impressionRef}>
        <div className="container lg:p-0">
          {/* Tag */}
          {promo.tag && (
            <Tag
              className="hidden rounded mb-2 lg:inline-block"
              severity={`${(promo?.tag as { tagName?: string })?.tagName?.toLowerCase() === 'trending' ? 'info' : 'warn'}`}
            >
              <span className="text-lg font-medium capitalize">
                {(promo?.tag as { tagName?: string })?.tagName?.toLowerCase()}
              </span>
            </Tag>
          )}

          {/* Header */}
          <div className="flex justify-between items-center mb-5 lg:mb-[30px]">
            <div>
              <h1
                style={{ overflowWrap: 'anywhere' }}
                data-testid="article-title"
                className="text-[16px] font-bold text-reliableBlack pr-8 lg:text-[32px] leading-[20px] lg:leading-[48px] lg:pr-4"
              >
                {promo.titlePage}
              </h1>
            </div>
            <div>
              {!enableSharing ? (
                <ShareButton onClick={() => setModalVisible(true)} aria-label="share button" />
              ) : (
                <ShareButton aria-label="share button" />
              )}
            </div>
          </div>

          {/* Date */}
          <div className="mb-7">
            <p className="text-xl font-semibold mb-2 hidden lg:block">Periode Promosi</p>
            <div className="flex items-center gap-2 text-reliableBlack90 text-sm lg:text-base">
              <MdOutlineCalendarMonth size={18} />
              <p data-testid="promo-period">
                {formattedStartDate} - {formattedEndDate}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="mb-5">
            <p className="max-w[768px]:text-base font-semibold text-reliableBlack mb-2.5 lg:text-xl">
              Detail Promosi
            </p>
            {!enableSharing ? (
              promo.detail && promo.detail.detailContent ? (
                <div id="detailContentPromo">
                  <div
                    style={{ overflowWrap: 'anywhere' }}
                    className="prose max-sm:prose-li:text-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: promo?.detail?.detailContent }}
                  />
                </div>
              ) : (
                ''
              )
            ) : (
              <div
                id="detailContentPromo"
                style={{ overflowWrap: 'anywhere' }}
                className="prose max-sm:prose-li:text-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: promo.detailContent }}
              ></div>
            )}
          </div>
        </div>
      </section>
      {/* Social media share modal */}
      {modalVisible && !enableSharing && (
        <ModalShare
          visible={modalVisible}
          link={`${URL}${pathname}`}
          onClose={() => setModalVisible(false)}
          actionClick={actionClick}
        />
      )}
    </>
  );
};

export default DetailPromoBodySection;
